import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const BRANDS = ["Volvo", "Mercedes", "Scania", "DAF", "Iveco", "MAN", "Renault", "Universeel"];
const ABBR = { Volvo: "VOL", Mercedes: "MB", Scania: "SCA", DAF: "DAF", Iveco: "IVE", MAN: "MAN", Renault: "REN", Universeel: "UNI" };
const CAT_ABBR = { Filters: "FIL", Remmen: "REM", Motor: "MOT", "Smeermiddelen & vloeistoffen": "OLI", "Elektrisch & verlichting": "ELE", "Ophanging & stuurinrichting": "OPH", "Koppeling & aandrijving": "KOP", "Luchtsysteem & remventielen": "LUC", "Uitlaat & AdBlue": "UIT", "Banden & wielen": "BAN", "Carrosserie & cabine": "CAR", "Bevestiging & klein materiaal": "BEV" };

/* House naming rule: "[Soort] [detail] – [Merk] [model] – [OEM]"  → e.g. "Oliefilter – Volvo FH D13 – 21707132" */
export const composeName = (d) => {
  const a = [d.part_type, d.part_detail].filter(Boolean).join(" ").trim();
  const b = [d.brand, d.model_fit].filter(Boolean).join(" ").trim();
  return [a, b, (d.oem_number || "").trim()].filter(Boolean).join(" – ");
};
export const composeSku = (d) => {
  const oem = (d.oem_number || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  if (!oem) return "";
  return [ABBR[d.brand] || (d.brand || "").slice(0, 3).toUpperCase() || "UNI", CAT_ABBR[d.category] || "ALG", oem].join("-");
};
/* Split a stored name back into its 3 visual segments for tidy display. */
export const nameParts = (name) => {
  const seg = String(name || "").split(" – ");
  return { type: seg[0] || "", fit: seg.length > 2 ? seg[1] : seg.length === 2 ? seg[1] : "", oem: seg.length > 2 ? seg.slice(2).join(" – ") : "" };
};

export const PartNameDisplay = ({ item, className = "" }) => {
  const p = nameParts(item.name);
  return (
    <div className={className} data-testid={`part-name-${item.sku}`}>
      <div className="font-semibold leading-tight">{p.type}</div>
      {(p.fit || p.oem) && (
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          {p.fit && <span className="text-[11px] font-medium text-primary">{p.fit}</span>}
          {p.oem && <span className="text-[10px] font-mono px-1.5 rounded bg-muted text-muted-foreground border border-border">{p.oem}</span>}
        </div>
      )}
      {item.name_ar && <div className="text-xs text-muted-foreground" dir="rtl">{item.name_ar}</div>}
    </div>
  );
};

const guessCategory = (type) => {
  const s = (type || "").toLowerCase();
  if (/filter|droger/.test(s)) return "Filters";
  if (/rem|brake/.test(s)) return "Remmen";
  if (/olie|vloeistof|vet|koelvloei|adblue/.test(s)) return /adblue|uitlaat/.test(s) ? "Uitlaat & AdBlue" : "Smeermiddelen & vloeistoffen";
  if (/lamp|accu|sensor|relais|zekering|kabel|led/.test(s)) return "Elektrisch & verlichting";
  if (/balg|ventiel|lucht|compressor/.test(s)) return "Luchtsysteem & remventielen";
  if (/veer|stabilis|fusee|stuur|schokdemper|draagarm/.test(s)) return "Ophanging & stuurinrichting";
  if (/koppeling|riem|spanrol|cardan|lager/.test(s)) return "Koppeling & aandrijving";
  if (/band|wiel|velg/.test(s)) return "Banden & wielen";
  if (/spiegel|bumper|ruit|cabine|deur|spatbord/.test(s)) return "Carrosserie & cabine";
  if (/bout|moer|klem|o-ring|ring|schroef/.test(s)) return "Bevestiging & klein materiaal";
  if (/turbo|injector|pomp|zuiger|pakking|thermostaat|motor/.test(s)) return "Motor";
  return null;
};

/* Form block: build the name from structured fields; name/SKU/category auto-fill while untouched. */
export const PartNameBuilder = ({ data, set, t, items = [] }) => {
  const types = [...new Set(items.map((i) => i.part_type || nameParts(i.name).type).filter(Boolean))].sort();
  const upd = (k, v) => {
    const next = { ...data, [k]: v };
    if (k === "part_type") { const g = guessCategory(v); if (g && (!data.category || data.category === "Algemeen" || data.__catAuto)) { next.category = g; next.__catAuto = true; } }
    const composed = composeName(next);
    set("__bulk", { ...next, name: composed || next.name, sku: data.sku && !data.__skuAuto ? data.sku : (composeSku(next) || data.sku), __skuAuto: true });
  };
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="part-name-builder">
      <div className="text-[10px] font-mono uppercase tracking-widest text-primary">{t("nameBuilderTitle")}</div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <div className="space-y-1"><Label className="text-[10px]">{t("nbType")} *</Label><Input list="nb-types" value={data.part_type || ""} onChange={(e) => upd("part_type", e.target.value)} placeholder="Oliefilter" data-testid="nb-type" /><datalist id="nb-types">{types.map((x) => <option key={x} value={x} />)}</datalist></div>
        <div className="space-y-1"><Label className="text-[10px]">{t("nbDetail")}</Label><Input value={data.part_detail || ""} onChange={(e) => upd("part_detail", e.target.value)} placeholder="vooras / 10W-40" data-testid="nb-detail" /></div>
        <div className="space-y-1"><Label className="text-[10px]">{t("nbBrand")}</Label><Input list="nb-brands" value={data.brand || ""} onChange={(e) => upd("brand", e.target.value)} placeholder="Volvo" data-testid="nb-brand" /><datalist id="nb-brands">{BRANDS.map((b) => <option key={b} value={b} />)}</datalist></div>
        <div className="space-y-1"><Label className="text-[10px]">{t("nbModel")}</Label><Input value={data.model_fit || ""} onChange={(e) => upd("model_fit", e.target.value)} placeholder="FH/FM D13" data-testid="nb-model" /></div>
        <div className="space-y-1"><Label className="text-[10px]">{t("nbOem")}</Label><Input value={data.oem_number || ""} onChange={(e) => upd("oem_number", e.target.value)} placeholder="21707132" className="font-mono" data-testid="nb-oem" /></div>
      </div>
      <div className="text-xs text-muted-foreground">{t("nbPreview")}: <span className="font-semibold text-foreground" data-testid="nb-preview">{composeName(data) || "—"}</span>{composeSku(data) ? <span className="font-mono ml-2 text-primary">{composeSku(data)}</span> : null}</div>
    </div>
  );
};
